import {
    afterFlipH,
    afterFlipV,
    afterRotate,
    getFrameAfterRotate,
    getPointsAfterRotate,
} from '@src/slide/infrastructure/module/independent/graphic';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { OperationType } from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';
import { IActiveSnapGridLine, LineType } from '@src/slide/application/service/snap-line-service/interface/line.interface';
import {
    EdgeType,
    OperationAnchor,
    Orient,
} from '@src/slide/application/service/snap-line-service/interface/orient.interface';
import type { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import type { IRefElement } from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import type { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import type {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type { IFlipInfo, ISizeInfo } from '@src/slide/application/service/edit-action-services/element-info';
import type { IMultiRect } from '@src/slide/application/service/edit-action-services/edit-process/edit-process.interface';
import type { IPoint } from '@src/slide/infrastructure/module/independent/graphic/point/point.interface';

/**
 * 根据 frame 和 rotate 获取旋转后的 frame
 */
export function getOuterFrameAfterRotate(frame: IFrame): IFrame {
    const { rotate } = frame;
    const { top = 0, left = 0, width = 0, height = 0 } = rotate ? getFrameAfterRotate(frame, rotate) : frame;

    return {
        rotate,
        ...frame,
        top,
        left,
        width,
        height,
    };
}

/**
 * 获取 ref 旋转后的 frame
 */
export function getRefFrameAfterRotate(ref: IRefElement): IFrame {
    const { ele } = ref;
    return getElementFrameAfterRotate(ele as IElementBaseModel);
}

export function getElementFrameAfterRotate(ele: IElementBaseModel) {
    const isGroupChildren = ele?.parent?.type === UIModelType.GROUP;

    return getOuterFrameAfterRotate(isGroupChildren
        ? (ele as IElementBaseModel).getFrame().frameOnSlide()
        : (ele.props.style || {}));
}

/**
 * 根据points，获取旋转后的点
 */
export function getAfterRotatePoints(
    originPoints: IPoint[], rotateAngle,
    centerPoint: IPoint | undefined = undefined,
): Array<IPoint> {
    let center = centerPoint;
    if (!center) {
        center = getCenterWithPoints(originPoints);
    }
    const points: Array<IPoint> = [];
    for (let i = 0; i < originPoints.length; i++) {
        points.push(afterRotate(originPoints[i], rotateAngle, center.x, center.y));
    }
    return points;
}

/**
 * 根据points，获取翻转后的点
 */
export function getAfterFlipPoints(
    originPoints: IPoint[],
    center: IPoint,
    flipV: boolean,
    flipH: boolean,
): Array<IPoint> {
    const points: Array<IPoint> = [];
    for (let i = 0; i < originPoints.length; i++) {
        let point = originPoints[i];
        if (flipH) {
            point = afterFlipH(point, center.x);
        }
        if (flipV) {
            point = afterFlipV(point, center.y);
        }
        points.push(point);
    }
    return points;
}


/**
 * 根据 nextSize，结合选中情况，获取实际在画布上进行吸附探测的 frame
 */
export function getDetectFrame(
    operationType: OperationType,
    element: IElementBaseModel,
    originSize: ISizeInfo & IFlipInfo,
    nextSize: ISizeInfo & IFlipInfo,
    multiRect: IMultiRect | null,
): IFrame {
    if (multiRect) {
        // 为组合或者多选的情况，需要考虑整体的multiRect
        // 多选的情况，move以multiRect为准，而resize的时候还是以操作的元素为准
        const shouldUseMultiFrame = (multiRect?.length > 1 && operationType === OperationType.MOVE)
            || element.type === UIModelType.GROUP;
        if (shouldUseMultiFrame) {
            return getNextMultiFrame(element, originSize, nextSize, multiRect);
        }
    }

    const frameOnSlide = getNextFrameOnSlide(element, originSize, nextSize);
    return getOuterFrameAfterRotate(frameOnSlide);
}

export function getNextFrameOnSlide(element: IElementBaseModel, originSize: IFrame, nextSize: IFrame) {
    // 组合子元素的情况，nextSize 和 originSize 都是相对父元素的，需要计算父元素位置
    const deltaSize = {
        left: nextSize.left - originSize.left,
        top: nextSize.top - originSize.top,
        width: nextSize.width - originSize.width,
        height: nextSize.height - originSize.height,
    };

    return element.getFrame().frameOnSlide({
        left: element.props.style.left + deltaSize.left,
        top: element.props.style.top + deltaSize.top,
        width: element.props.style.width + deltaSize.width,
        height: element.props.style.height + deltaSize.height,
        rotate: element.props.style.rotate,
        flipH: nextSize.flipH,
        flipV: nextSize.flipV,
    });
}

/**
 * 结合nextSize和 originSize，计算新的 MultiFrame
 */
export function getNextMultiFrame(
    element: IElementBaseModel,
    originSize: ISizeInfo & IFlipInfo,
    nextSize: ISizeInfo & IFlipInfo,
    multiRect: IMultiRect,
): IFrame {
    const originFrame = getOuterFrameAfterRotate({
        rotate: element.props.style.rotate,
        ...originSize,
    });
    const nextFrame = getOuterFrameAfterRotate({
        rotate: element.props.style.rotate,
        ...nextSize,
    });

    return {
        ...originFrame,
        ...nextFrame,
        left: (nextFrame.left - originFrame.left + multiRect.left),
        top: (nextFrame.top - originFrame.top + multiRect.top),
        width: (nextFrame.width - originFrame.width + multiRect.width),
        height: (nextFrame.height - originFrame.height + multiRect.height),
    };
}

/**
 * 根据四个点，获取 frame
 */
export function getFrameWithPoints(points: IPoint[]) {
    const minX = Math.min(...points.map(point => point.x));
    const maxX = Math.max(...points.map(point => point.x));
    const minY = Math.min(...points.map(point => point.y));
    const maxY = Math.max(...points.map(point => point.y));

    return {
        left: minX,
        top: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

export function isLessOrEqual(positionA: number, positionB: number) {
    const epsilon = 1e-5;
    return positionA <= positionB || Math.abs(positionA - positionB) <= epsilon;
}

export function getFrameAllGrids(frame: IFrame) {
    const { top, left, width, height, flipH, flipV } = frame;
    const topGrid = {
        orient: Orient.HORIZONTAL, position: top, type: LineType.EDGE, fromEdge: EdgeType.TOP };
    const bottomGrid = {
        orient: Orient.HORIZONTAL, position: top + height, type: LineType.EDGE, fromEdge: EdgeType.BOTTOM };
    const leftGrid = {
        orient: Orient.VERTICAL, position: left, type: LineType.EDGE, fromEdge: EdgeType.LEFT };
    const rightGrid = {
        orient: Orient.VERTICAL, position: left + width, type: LineType.EDGE, fromEdge: EdgeType.RIGHT };
    const verticalBaseLineGrid = {
        orient: Orient.VERTICAL,
        position: left + (width / 2),
        type: LineType.BASE_LINE }
    ;
    const horizontalBaseLineGrid = {
        orient: Orient.HORIZONTAL,
        position: top + (height / 2),
        type: LineType.BASE_LINE }
    ;

    return {
        topGrid,
        bottomGrid,
        leftGrid,
        rightGrid,
        verticalBaseLineGrid,
        horizontalBaseLineGrid,
    };
}

export function getParentGroupStyle(ele: IElementBaseModel) {
    if (ele.parent?.type !== UIModelType.GROUP) return undefined;

    return (ele.parent as IGroupModel).props.style;
}

/**
 * 获取在画布上，frame最终显示的四个点位置
 */
export function getFramePointsOnSlide(frameOnSlide: IFrame, rotateOnSlide: number): IPoint[] {
    const originPoints = getPointsWithFrame(frameOnSlide);

    const flippedPoints = getAfterFlipPoints(
        originPoints,
        getCenterWithPoints(originPoints),
        !!frameOnSlide.flipV,
        !!frameOnSlide.flipH,
    );

    return getAfterRotatePoints(flippedPoints, rotateOnSlide);
}

/*
 * 获取 frame 的四个点坐标
 */
export function getPointsWithFrame(frame: IFrame) {
    return getPointsAfterRotate(frame, 0);
}

export function getCenterWithPoints(points: IPoint[]): IPoint {
    const [p1, p2, p3, p4] = points;
    const x1 = p1.x;
    const y1 = p1.y;
    const x2 = p2.x;
    const y2 = p2.y;
    const x3 = p3.x;
    const y3 = p3.y;
    const x4 = p4.x;
    const y4 = p4.y;

    const x = (x1 + x2 + x3 + x4) / 4;
    const y = (y1 + y2 + y3 + y4) / 4;

    return { x, y };
}

/**
 * frame是否被 otherFrame完全遮盖
 */
export function isCompleteCoveredBy(frame: IFrame, otherFrame: IFrame) {
    return otherFrame.left <= frame.left
        && otherFrame.top <= frame.top
        && otherFrame.width + otherFrame.left >= frame.width + frame.left
        && otherFrame.top + otherFrame.height >= frame.top + frame.height;
}


export function autoFitNoChangeAspectOffsetFrame(
    snapLines: IActiveSnapGridLine[],
    offsetFrame: any,
    anchor: OperationAnchor,
    scale: number,
) {
    handleHorizontalAdsorbInAspect(snapLines, offsetFrame, anchor, scale);
    handleVerticalAdsorbInAspect(snapLines, offsetFrame, anchor, scale);
}

export function handleVerticalAdsorbInAspect(
    snapLines: IActiveSnapGridLine[],
    offsetFrame: any,
    anchor: OperationAnchor,
    scale: number,
) {
    if (snapLines?.some(line => line.orient === Orient.HORIZONTAL)) {
        switch (anchor) {
            case OperationAnchor.LEFT_TOP:
                offsetFrame.width = offsetFrame.height / scale;
                offsetFrame.left = offsetFrame.top / scale;
                break;
            case OperationAnchor.RIGHT_TOP:
                offsetFrame.width = offsetFrame.height / scale;
                break;
            case OperationAnchor.LEFT_BOTTOM:
                offsetFrame.width = offsetFrame.height / scale;
                offsetFrame.left = -offsetFrame.width;
                break;
            case OperationAnchor.RIGHT_BOTTOM:
                offsetFrame.width = offsetFrame.height / scale;
                break;
            default:
                break;
        }
    }
}

export function handleHorizontalAdsorbInAspect(
    snapLines: IActiveSnapGridLine[],
    offsetFrame: any,
    anchor: OperationAnchor,
    scale: number,
) {
    if (snapLines?.some(line => line.orient === Orient.VERTICAL)) {
        switch (anchor) {
            case OperationAnchor.LEFT_TOP:
                offsetFrame.height = offsetFrame.width * scale;
                offsetFrame.top = offsetFrame.left * scale;
                break;
            case OperationAnchor.RIGHT_TOP:
                offsetFrame.height = offsetFrame.width * scale;
                offsetFrame.top = -offsetFrame.height;
                break;
            case OperationAnchor.LEFT_BOTTOM:
                offsetFrame.height = offsetFrame.width * scale;
                break;
            case OperationAnchor.RIGHT_BOTTOM:
                offsetFrame.height = offsetFrame.width * scale;
                break;
            default:
                break;
        }
    }
}
